import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Divider,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useFormik, FormikProvider, FormikProps, useFormikContext } from "formik";
import * as Yup from "yup";
import { UserRole, UserRoles } from "../../../constants/roles";
import { useAppSelector } from "../../../app/hooks";
import { GenericTextField } from "../../../components/common/FormFields";
import GenericDateField from "../../../components/common/FormFields/GenericDateField";
import GenericMonthDayField from "../../../components/common/FormFields/GenericMonthDayField";
import { userFormValidationSchema, getInitialUserFormValues } from "../validation/userFormValidation";
import { UserFormLayoutProps, UserFormContentProps, UserFormValues } from "../types/userFormTypes";
import { useResendPasswordSetupEmailMutation } from "../../../api/usersApi";
import { AddButton } from "../../../components/common/AddButton";

// Component that returns Formik instance for use with BaseFormPage
export const UserFormContent: React.FC<UserFormContentProps> = ({
  mode,
  initialValues,
  onSubmit,
  isLoading = false,
  error,
  onClearError,
  onFormikReady,
  userId = 0,
  emailConfirmed = false,
  originalEmail = "",
}) => {
  const isEditing = mode === "edit";
  const [resendSuccess, setResendSuccess] = useState(false);

  // Use parent Formik instance provided by BaseFormPage
  const formik = useFormikContext<UserFormValues>();

  const currentUserRole = useAppSelector((state) => state.auth.user?.role);
  const isAdminOrSuperAdmin =
    currentUserRole === UserRoles.ADMIN || currentUserRole === UserRoles.SUPER_ADMIN;

  // Resend password setup email mutation
  const [resendPasswordSetupEmail, { isLoading: isResending }] = useResendPasswordSetupEmailMutation();

  // Handle resend email
  const handleResendEmail = async () => {
    if (!formik.values.email || !userId) return;

    try {
      await resendPasswordSetupEmail({
        id: userId,
        email: formik.values.email
      }).unwrap();
      setResendSuccess(true);
    } catch (error: any) {
      // Silently fail - no error message needed
    }
  };

  // Check if resend button should be shown (always show in edit mode)
  const shouldShowResendButton = isEditing;

  // Check if button should be enabled
  const canSendEmail = shouldShowResendButton && formik.values.email && !isResending && (
    !emailConfirmed ||
    formik.values.email !== originalEmail
  );

  // Clear error when user starts typing
  useEffect(() => {
    if (error && onClearError) {
      const handleFieldChange = () => {
        onClearError();
      };

      // Add event listeners to form fields
      const form = document.querySelector('form');
      if (form) {
        form.addEventListener('input', handleFieldChange);
        return () => form.removeEventListener('input', handleFieldChange);
      }
    }
  }, [error, onClearError]);

  // If no Formik context is available, show loading state
  if (!formik) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const isEmployee = formik.values.isEmployee;
  const isTruckDriver = formik.values.role === UserRoles.TRUCK_DRIVER;
  const showDriverInfo = isEmployee && isTruckDriver;

  return (
    <Box>
      {/* Success Snackbar */}
      <Snackbar
        open={resendSuccess}
        autoHideDuration={6000}
        onClose={() => setResendSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setResendSuccess(false)} severity="success">
          Password setup email sent successfully!
        </Alert>
      </Snackbar>

      {/* User Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            User Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <GenericTextField
                name="firstName"
                label="First Name"
                required
                placeholder="Enter first name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenericTextField
                name="lastName"
                label="Last Name"
                required
                placeholder="Enter last name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <GenericTextField
                    name="email"
                    label="Email Address"
                    type="email"
                    required
                    placeholder="Enter email address"
                  />
                </Box>
                {shouldShowResendButton && (
                  <AddButton
                    onClick={handleResendEmail}
                    disabled={!canSendEmail}
                    size="small"
                    variant="contained"
                    color="primary"
                    showIcon={false}
                    sx={{
                      minWidth: 'auto',
                      px: 2,
                      mt: 0.5
                    }}
                  >
                    {isResending ? "Sending..." : "Resend Password Setup Email"}
                  </AddButton>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={formik.touched.role && Boolean(formik.errors.role)}
                sx={{ mb: 2 }}
              >
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  id="role"
                  name="role"
                  value={formik.values.role}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    formik.setFieldValue("role", newRole);
                    if (newRole === UserRoles.ADMIN) {
                      formik.setFieldValue("isEmployee", false);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  label="Role"
                  disabled={!isAdminOrSuperAdmin}
                >
                  {isAdminOrSuperAdmin && (
                    <MenuItem value={UserRoles.ADMIN}>Admin</MenuItem>
                  )}
                  {isAdminOrSuperAdmin && (
                    <MenuItem value={UserRoles.DISPATCHER}>Dispatcher</MenuItem>
                  )}
                  <MenuItem value={UserRoles.TRUCK_DRIVER}>Truck Driver</MenuItem>
                  {isAdminOrSuperAdmin && (
                    <MenuItem value={UserRoles.FLEET}>Fleet</MenuItem>
                  )}
                  {isAdminOrSuperAdmin && (
                    <MenuItem value={UserRoles.SAFETY}>Safety</MenuItem>
                  )}
                  {isAdminOrSuperAdmin && (
                    <MenuItem value={UserRoles.RECRUITING}>Recruiting</MenuItem>
                  )}
                  {isAdminOrSuperAdmin && (
                    <MenuItem value={UserRoles.ACCOUNTING}>Accounting</MenuItem>
                  )}
                  {isAdminOrSuperAdmin && (
                    <MenuItem value={UserRoles.IFTA_ADMIN}>IFTA Admin</MenuItem>
                  )}
                </Select>
                {formik.touched.role && formik.errors.role && (
                  <FormHelperText>{formik.errors.role}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Employee Information Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Employee Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isEmployee"
                    checked={isEmployee}
                    disabled={formik.values.role === UserRoles.ADMIN}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                }
                label="Is employed in company"
              />
            </Grid>

            {isEmployee && (
              <>
                <Grid item xs={12} sm={6}>
                  <GenericDateField
                    name="employmentStartDate"
                    label="Employment Start Date"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericDateField
                    name="employmentEndDate"
                    label="Employment End Date"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericDateField
                    name="dateOfBirth"
                    label="Date of Birth"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericMonthDayField
                    name="familyHoliday"
                    label="Family Holiday"
                    tooltip="Slava day"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="phoneNumber"
                    label="Phone number"
                    placeholder="Mobile or direct work phone"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="worksWeekends"
                        checked={formik.values.worksWeekends}
                        onChange={formik.handleChange}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          Works Weekends
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, weekend days are included in absence day calculations and balance deductions.
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Address Section - Only show when employee */}
      {isEmployee && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Address Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <GenericTextField
                  name="address.street"
                  label="Street Address"
                  placeholder="Enter street address"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="address.city"
                  label="City"
                  placeholder="Enter city"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="address.state"
                  label="State"
                  placeholder="Enter state"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="address.zipCode"
                  label="ZIP Code"
                  placeholder="Enter ZIP code"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="address.country"
                  label="Country"
                  placeholder="Enter country"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Emergency Contact Section - Only show when employee */}
      {isEmployee && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Emergency Contact
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="emergencyContact.name"
                  label="Emergency Contact Name"
                  placeholder="Enter emergency contact name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="emergencyContact.phone"
                  label="Emergency Contact Phone"
                  placeholder="Enter emergency contact phone"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Driver Information Section - Only show when employee and truck driver */}
      {showDriverInfo && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Driver Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <GenericTextField
                  name="driverInfo.companyName"
                  label="Company Name"
                  placeholder="Enter company name (optional)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="driverInfo.ein"
                  label="EIN Number"
                  placeholder="Enter EIN number"
                  tooltip="Either EIN or SSN is required"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="driverInfo.ssn"
                  label="SSN"
                  placeholder="Enter SSN"
                  tooltip="Either EIN or SSN is required"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// Original UserFormLayout component (for backward compatibility)
const UserFormLayout: React.FC<UserFormLayoutProps> = ({
  mode,
  initialValues,
  onSubmit,
  isLoading = false,
  error,
  onClearError,
}) => {
  const isEditing = mode === "edit";

  const formik = useFormik<UserFormValues>({
    initialValues: {
      ...getInitialUserFormValues(),
      ...initialValues,
    },
    validationSchema: userFormValidationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Clear error when user starts typing
  useEffect(() => {
    if (error && onClearError) {
      const handleFieldChange = () => {
        onClearError();
      };

      // Add event listeners to form fields
      const form = document.querySelector('form');
      if (form) {
        form.addEventListener('input', handleFieldChange);
        return () => form.removeEventListener('input', handleFieldChange);
      }
    }
  }, [error, onClearError]);

  const isEmployee = formik.values.isEmployee;
  const isTruckDriver = formik.values.role === UserRoles.TRUCK_DRIVER;
  const showDriverInfo = isEmployee && isTruckDriver;

  return (
    <FormikProvider value={formik}>
      <Box component="form" onSubmit={formik.handleSubmit}>
        {/* User Information Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              User Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="firstName"
                  label="First Name"
                  required
                  placeholder="Enter first name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="lastName"
                  label="Last Name"
                  required
                  placeholder="Enter last name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField
                  name="email"
                  label="Email Address"
                  type="email"
                  required
                  placeholder="Enter email address"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={formik.touched.role && Boolean(formik.errors.role)}
                  sx={{ mb: 2 }}
                >
                  <InputLabel id="role-label">Role</InputLabel>
                  <Select
                    labelId="role-label"
                    id="role"
                    name="role"
                    value={formik.values.role}
                    onChange={(e) => {
                      const newRole = e.target.value;
                      formik.setFieldValue("role", newRole);
                      if (newRole === UserRoles.ADMIN) {
                        formik.setFieldValue("isEmployee", false);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    label="Role"
                  >
                    <MenuItem value={UserRoles.ADMIN}>
                      Admin
                    </MenuItem>
                    <MenuItem value={UserRoles.DISPATCHER}>
                      Dispatcher
                    </MenuItem>
                    <MenuItem value={UserRoles.TRUCK_DRIVER}>
                      Truck Driver
                    </MenuItem>
                    <MenuItem value={UserRoles.FLEET}>
                      Fleet
                    </MenuItem>
                    <MenuItem value={UserRoles.SAFETY}>
                      Safety
                    </MenuItem>
                    <MenuItem value={UserRoles.RECRUITING}>
                      Recruiting
                    </MenuItem>
                    <MenuItem value={UserRoles.ACCOUNTING}>
                      Accounting
                    </MenuItem>
                    <MenuItem value={UserRoles.IFTA_ADMIN}>
                      IFTA Admin
                    </MenuItem>
                  </Select>
                  {formik.touched.role && formik.errors.role && (
                    <FormHelperText>{formik.errors.role}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Employee Information Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Employee Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isEmployee"
                      checked={isEmployee}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  }
                  label="Is employed in company"
                />
              </Grid>

              {isEmployee && (
                <>
                  <Grid item xs={12} sm={6}>
                    <GenericDateField
                      name="employmentStartDate"
                      label="Employment Start Date"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GenericDateField
                      name="employmentEndDate"
                      label="Employment End Date"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GenericDateField
                      name="dateOfBirth"
                      label="Date of Birth"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GenericMonthDayField
                      name="familyHoliday"
                      label="Family Holiday"
                      tooltip="Slava day"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <GenericTextField
                      name="phoneNumber"
                      label="Phone number"
                      placeholder="Mobile or direct work phone"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="worksWeekends"
                          checked={formik.values.worksWeekends}
                          onChange={formik.handleChange}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            Works Weekends
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            When enabled, weekend days are included in absence day calculations and balance deductions.
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Address Section - Only show when employee */}
        {isEmployee && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Address Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <GenericTextField
                    name="address.street"
                    label="Street Address"
                    placeholder="Enter street address"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="address.city"
                    label="City"
                    placeholder="Enter city"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="address.state"
                    label="State"
                    placeholder="Enter state"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="address.zipCode"
                    label="ZIP Code"
                    placeholder="Enter ZIP code"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="address.country"
                    label="Country"
                    placeholder="Enter country"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Emergency Contact Section - Only show when employee */}
        {isEmployee && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Emergency Contact
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="emergencyContact.name"
                    label="Emergency Contact Name"
                    placeholder="Enter emergency contact name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="emergencyContact.phone"
                    label="Emergency Contact Phone"
                    placeholder="Enter emergency contact phone"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Driver Information Section - Only show when employee and truck driver */}
        {showDriverInfo && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Driver Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <GenericTextField
                    name="driverInfo.companyName"
                    label="Company Name"
                    placeholder="Enter company name (optional)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="driverInfo.ein"
                    label="EIN Number"
                    placeholder="Enter EIN number"
                    tooltip="Either EIN or SSN is required"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GenericTextField
                    name="driverInfo.ssn"
                    label="SSN"
                    placeholder="Enter SSN"
                    tooltip="Either EIN or SSN is required"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>
    </FormikProvider>
  );
};

export default UserFormLayout;
